import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { IdeaBadges } from '@/components/idea-badges';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import { useAuthStore } from '@/store/auth-store';
import { useIdeasStore } from '@/store/ideas-store';
import { colors, radius, spacing } from '@/theme/tokens';
import { formatWhenPl } from '@/utils/format';

function initials(name: string): string {
  return name
    .split(' ')
    .map((p) => p.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Idea detail + voting + comments — mock: szczegóły_zgłoszenia. */
export default function IdeaDetailScreen() {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams<{ id: string }>();
  const idea = useIdeasStore((s) => s.ideas.find((i) => i.id === id));
  const toggleVote = useIdeasStore((s) => s.toggleVote);
  const addComment = useIdeasStore((s) => s.addComment);
  const loadDetail = useIdeasStore((s) => s.loadDetail);
  const account = useAuthStore((s) => s.account);

  const [draft, setDraft] = useState('');
  const authorName = account?.firstName ?? t('ideas.you');

  // Backend mode: pull the latest comments/votes for this item (no-op offline).
  useEffect(() => {
    if (id) void loadDetail(id);
  }, [id, loadDetail]);

  if (!idea) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
        <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md }}>
          <ScreenHeader title={t('ideas.detailTitle')} />
          <AppText variant="bodyLg">{t('ideas.notFound')}</AppText>
        </ScrollView>
      </SafeAreaView>
    );
  }

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    await addComment(idea.id, text, authorName);
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}
        >
          <ScreenHeader title={t('ideas.detailTitle')} />

          <Card style={{ gap: spacing.sm }}>
            <IdeaBadges kind={idea.kind} status={idea.status} solid />
            <AppText variant="headlineLg">{idea.title}</AppText>
            <AppText variant="bodyLg">{idea.description}</AppText>
            <Pressable
              accessibilityRole="button"
              onPress={() => toggleVote(idea.id)}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: spacing.xs,
                backgroundColor: idea.votedByMe ? colors.primary : colors.navy,
                borderRadius: radius.button,
                paddingVertical: spacing.md,
                minHeight: 52,
                marginTop: spacing.xs,
                transform: [{ scale: pressed ? 0.98 : 1 }],
              })}
            >
              <Ionicons name={idea.votedByMe ? 'checkmark' : 'chevron-up'} size={20} color="#FFFFFF" />
              <AppText variant="labelLg" color="#FFFFFF">
                {idea.votedByMe
                  ? t('ideas.voted', { count: idea.votes })
                  : t('ideas.vote', { count: idea.votes })}
              </AppText>
            </Pressable>
          </Card>

          <AppText variant="headlineSm">{t('ideas.comments')}</AppText>
          {idea.comments.length === 0 && (
            <AppText variant="bodyMd" color={colors.outline}>
              {t('ideas.noComments')}
            </AppText>
          )}
          {idea.comments.map((c) => (
            <Card key={c.id} style={{ flexDirection: 'row', gap: spacing.sm }}>
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: radius.full,
                  backgroundColor: colors.primaryTile,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AppText variant="labelLg" color={colors.navy}>
                  {initials(c.author)}
                </AppText>
              </View>
              <View style={{ flex: 1, gap: 2 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <AppText variant="labelLg">{c.author}</AppText>
                  <AppText variant="labelMd" color={colors.outline}>
                    {formatWhenPl(c.createdAt)}
                  </AppText>
                </View>
                <AppText variant="bodyMd">{c.text}</AppText>
              </View>
            </Card>
          ))}
        </ScrollView>

        {/* Comment composer pinned to the bottom */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: spacing.sm,
            paddingHorizontal: spacing.screen,
            paddingBottom: spacing.md,
            paddingTop: spacing.xs,
          }}
        >
          <TextField
            placeholder={t('ideas.addComment')}
            value={draft}
            onChangeText={setDraft}
            style={{ flex: 1, minHeight: 48 }}
            onSubmitEditing={send}
            returnKeyType="send"
          />
          <Pressable
            accessibilityRole="button"
            onPress={send}
            style={{
              width: 48,
              height: 48,
              borderRadius: radius.full,
              backgroundColor: colors.navy,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Ionicons name="send" size={18} color="#FFFFFF" />
          </Pressable>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
